import DOMPurify from 'dompurify';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { chunkPages, paginate } from './pdfPagination';
import { collectBoxes, collectTextRuns, drawBoxes, drawMarginText, drawPhotos, drawSvgPictures, drawTextRuns, drawWatermark, measurePhotos, rasterizeSvgElements, resetWordPictures, VECTOR_FONT_CSS, watermarkOf, type EncodedPhoto } from './pdfVector';

// Device-side PDF rendering (Final Authority §20.5: offline, no server rendering). The report or agreement
// HTML is laid out in an off-screen container, photographed by html2canvas onto real canvases, and every
// page goes into the PDF as one image. The picture keeps the Chinese typography and the DEMO watermark
// exactly as they are on screen without shipping a CJK font into the PDF engine.
//
// DT-06 (found 2026-09-12; present since the first device build): this file used to hand the container to
// jsPDF's html(). That does NOT rasterize — it re-draws the page as Helvetica vector text, so every Chinese
// character came out as garbage and CSS backgrounds (the watermark) were dropped — and the container's
// opacity:0 made html2canvas draw nothing at all: every PDF was blank pages that still had bytes and a page
// count. The container stays invisible here; only the COPY html2canvas renders is made visible (onclone).
//
// iOS WebKit refuses canvases above 16,777,216 pixels (the canvas silently stays blank), so a long document
// is drawn in chunks of whole pages well below that, and each page is cut from its chunk. Where a page ends
// is decided in ./pdfPagination.ts: between two lines, never through a photo, a signature box or a row.
//
// Every library is imported STATICALLY and handed to jsPDF through the globals it looks for first
// (loadOptionalLibrary): jsPDF would otherwise `import()` html2canvas / dompurify at call time, and a
// dynamic chunk that stalls on device is a documented legacy failure mode (iOS picker hang) that this
// build does not reproduce. The production bundle therefore carries no lazily loaded PDF chunk.
declare global {
  interface Window {
    html2canvas?: typeof html2canvas;
    DOMPurify?: typeof DOMPurify;
  }
}

function bindOptionalLibraries(): void {
  if (typeof window === 'undefined') return;
  if (!window.html2canvas) window.html2canvas = html2canvas;
  if (!window.DOMPurify) window.DOMPurify = DOMPurify;
}

export interface PdfResult {
  bytes: ArrayBuffer;
  pageCount: number;
}

export interface PdfRenderer {
  render(html: string): Promise<PdfResult>;
}

const PAGE_WIDTH_PT = 612; // US Letter
const PAGE_HEIGHT_PT = 792;
const MARGIN_PT = 36;
const MARGIN_BOTTOM_PT = MARGIN_PT + 8;
const LAYOUT_WIDTH_PX = 720;
const CONTENT_WIDTH_PT = PAGE_WIDTH_PT - MARGIN_PT * 2;
const PT_PER_PX = CONTENT_WIDTH_PT / LAYOUT_WIDTH_PX;
// The printable height of one page, in layout pixels.
export const PDF_PAGE_HEIGHT_PX = Math.floor((PAGE_HEIGHT_PT - MARGIN_PT - MARGIN_BOTTOM_PT) / PT_PER_PX);
// Device pixels per layout pixel — about 192 dpi on paper, sharp for the smallest print in the report.
const SCALE = 2;
// One canvas never holds more than this many pixels: well under WebKit's 16,777,216-pixel ceiling.
export const PDF_MAX_CANVAS_PIXELS = 12_000_000;
// A page may end at the top or the bottom of these blocks, never inside them; a heading keeps the first
// line under it on its page.
const UNBREAKABLE = 'img, svg, tr, .rp-photo, .rp-obs-row, .rp-stat, .rp-lim, .ag-sigbox, .ag-consent-box, .ag-demo-banner, .rp-demo-banner, .ag-lang-tag';
const HEADINGS = 'h1, h2, h3, h4';
const KEEP_WITH_NEXT_PX = 40;
// html2canvas finds each font's baseline with a 1×1 GIF placed after a line of sample text, appended to
// THIS document's body (html2canvas 1.4.1, FontMetrics.parseMetrics). The app's global
// `img { display: block }` (src/ui/theme.css) put that probe on a line of its own, so html2canvas drew every
// line of text about a line-height too low: pages broke through lines, labels sat under their outlines.
// While the renderer runs, the probe is inline again.
const FONT_PROBE_FIX = 'body > div > img[src^="data:image/gif;base64"] { display: inline !important; }';
// An English document with more Chinese characters than this is printed as page pictures (see render()).
const VECTOR_MAX_CJK = 200;

// ---------- 2026-10-19 (Owner item 20): running header / footer and the invisible text layer ----------

// The running header and footer of a report (data attributes on its root, src/report/renderHtml.ts) are
// drawn in each page's margin as a crisp strip — on a canvas, so a Chinese company name prints too —
// with "Page n of N" at the right of the header.
const STRIP_HEIGHT_PX = 16;

function marginStrip(left: string, right: string): string | null {
  const canvas = document.createElement('canvas');
  canvas.width = LAYOUT_WIDTH_PX * SCALE;
  canvas.height = STRIP_HEIGHT_PX * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#707973';
  ctx.font = `${10 * SCALE}px Inter, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', system-ui, sans-serif`;
  ctx.textBaseline = 'middle';
  const y = canvas.height / 2;
  const rightWidth = right ? ctx.measureText(right).width : 0;
  // the left text never runs into the page number
  let text = left;
  const room = canvas.width - rightWidth - 16 * SCALE;
  while (text.length > 1 && ctx.measureText(text).width > room) text = `${text.slice(0, -2)}…`;
  ctx.textAlign = 'left';
  ctx.fillText(text, 0, y);
  if (right) {
    ctx.textAlign = 'right';
    ctx.fillText(right, canvas.width, y);
  }
  try {
    return canvas.toDataURL('image/png');
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}


// ---------- iPhone real-photo run 2026-10-26: SVG backgrounds become PNGs before html2canvas ----------
//
// 2.0.0 (208) on iOS 27.0.1: every DEMO PDF failed with "The operation is insecure.". html2canvas paints a
// CSS background image through createPattern(), and WebKit marks a canvas that took a pattern of an SVG
// image as unsafe — toDataURL() then throws. Drawing the same SVG straight onto a canvas is safe, so each
// SVG background (the DEMO watermark, src/report/watermark.ts) is drawn into a PNG of its own first and the
// copy html2canvas photographs tiles that PNG (docs/qa/evidence/iphone-real-photos-2026-10-26: B3–B5 fail,
// B6 passes). The HTML itself keeps the SVG: the in-app preview shows it and the agreement's fingerprint
// (src/state/agreementPdfStore.ts) is its text.
const SVG_BACKGROUND = /url\(\s*(['"]?)(data:image\/svg\+xml[^'")]*)\1\s*\)/;
const SVG_LOAD_TIMEOUT_MS = 10_000;

export type SvgToPng = (svgUrl: string, width: number, height: number) => Promise<string>;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error('svg background did not load')), SVG_LOAD_TIMEOUT_MS);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('svg background did not load'));
    };
    img.src = src;
  });
}

// One tile at the PDF's resolution (SCALE device pixels per layout pixel), so the watermark stays sharp.
const svgTileToPng: SvgToPng = async (svgUrl, width, height) => {
  const img = await loadImage(svgUrl);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round((width || img.naturalWidth) * SCALE));
  canvas.height = Math.max(1, Math.round((height || img.naturalHeight) * SCALE));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d canvas for the svg background');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  try {
    return canvas.toDataURL('image/png');
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
};

// The tile size the element asks for ("420px 260px"); 0 when it leaves it to the image.
function backgroundTileSize(el: HTMLElement): [number, number] {
  const [w, h] = el.style.backgroundSize.split(/\s+/).map((part) => (part.endsWith('px') ? parseFloat(part) : 0));
  return [Number.isFinite(w) ? w : 0, Number.isFinite(h) ? h : Number.isFinite(w) ? w : 0];
}

// Every inline SVG background under root is replaced by its PNG (one drawing per distinct tile). A tile that
// cannot be drawn keeps its SVG: other engines photograph it as before.
export async function rasterizeSvgBackgrounds(root: HTMLElement, toPng: SvgToPng = svgTileToPng): Promise<number> {
  const drawn = new Map<string, Promise<string | null>>();
  let replaced = 0;
  for (const el of [root, ...Array.from(root.querySelectorAll<HTMLElement>('[style]'))]) {
    const match = SVG_BACKGROUND.exec(el.style.backgroundImage);
    if (!match) continue;
    const [width, height] = backgroundTileSize(el);
    const key = `${width}x${height} ${match[2]}`;
    if (!drawn.has(key)) drawn.set(key, toPng(match[2], width, height).catch(() => null));
    const png = await drawn.get(key);
    if (!png) continue;
    el.style.backgroundImage = `url("${png}")`;
    replaced += 1;
  }
  return replaced;
}

// ---------- Timing walk 2026-10-28: only the photos of the chunk being drawn carry their picture ----------
//
// TestFlight 2.0.0 (212), 85 real photos — 166 pictures in a 49.6 MB report HTML: both 生成最终报告 attempts
// ended with the web content process killed at its 1,536 MB limit, in the app and in iPhone Safari alike
// (docs/qa/evidence/timing-walk-2026-10-28). html2canvas clones the whole document for every chunk, loads
// every <img> of the clone and paints every one of them, so each chunk paid for every photo of the report.
// Now every data: picture becomes one blob: URL (the base64 text is not carried around), every picture's
// laid-out box is fixed once the pages are measured, and before a chunk is drawn only the pictures inside it
// show their image — every other one shows a hidden 1×1 placeholder in the same box. What a chunk costs
// depends on its own pages, not on how many photos the inspection has.
const PLACEHOLDER_SRC = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function dataUrlToBlob(dataUrl: string): Blob | null {
  const match = /^data:([^;,]*)(;base64)?,([\s\S]*)$/.exec(dataUrl);
  if (!match) return null;
  try {
    if (!match[2]) return new Blob([decodeURIComponent(match[3])], { type: match[1] || 'text/plain' });
    const binary = atob(match[3]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: match[1] || 'application/octet-stream' });
  } catch {
    return null;
  }
}

// Every <img> under root whose source is a data: URL gets a blob: URL instead (one per distinct picture).
// Returns the URLs made, for the caller to revoke. Where blob URLs do not exist the pictures stay as they are.
export function pictureSourcesToBlobUrls(root: ParentNode): string[] {
  if (typeof URL.createObjectURL !== 'function') return [];
  const made = new Map<string, string>();
  for (const img of Array.from(root.querySelectorAll('img'))) {
    const src = img.getAttribute('src') ?? '';
    if (!src.startsWith('data:')) continue;
    let url = made.get(src);
    if (!url) {
      const blob = dataUrlToBlob(src);
      if (!blob) continue;
      url = URL.createObjectURL(blob);
      made.set(src, url);
    }
    img.setAttribute('src', url);
  }
  return [...made.values()];
}

export interface PictureSlot {
  img: HTMLImageElement;
  src: string;
  // the box, relative to the document's top (layout px)
  top: number;
  bottom: number;
}

// The pictures of the laid-out document with their boxes fixed to the size they have now, so a placeholder
// in the same element changes nothing on the page.
export function freezePictures(content: HTMLElement): PictureSlot[] {
  const origin = content.getBoundingClientRect().top;
  const slots: PictureSlot[] = [];
  for (const img of Array.from(content.querySelectorAll('img'))) {
    const src = img.getAttribute('src') ?? '';
    if (src.length === 0) continue;
    const rect = img.getBoundingClientRect();
    img.style.width = `${rect.width}px`;
    img.style.height = `${rect.height}px`;
    slots.push({ img, src, top: rect.top - origin, bottom: rect.bottom - origin });
  }
  return slots;
}

// Before a chunk [start, end) is drawn: the pictures inside it show their image, every other one the hidden
// placeholder (html2canvas does not paint what is hidden). Returns how many carry their picture.
export function showPicturesOf(slots: readonly PictureSlot[], start: number, end: number): number {
  let shown = 0;
  for (const slot of slots) {
    const inside = slot.bottom > start && slot.top < end;
    const want = inside ? slot.src : PLACEHOLDER_SRC;
    if (slot.img.getAttribute('src') !== want) slot.img.setAttribute('src', want);
    slot.img.style.visibility = inside ? '' : 'hidden';
    if (inside) shown += 1;
  }
  return shown;
}

function imagesDecoded(root: HTMLElement): Promise<void> {
  const pending = Array.from(root.querySelectorAll('img')).filter((img) => !img.complete);
  return Promise.all(
    pending.map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        })
    )
  ).then(() => undefined);
}

// The bottom of every line and the extent of every unbreakable block, relative to the document's top.
function measureBreaks(content: HTMLElement): { lineBottoms: number[]; atomic: Array<[number, number]> } {
  const origin = content.getBoundingClientRect().top;
  const lineBottoms: number[] = [];
  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if ((node.textContent ?? '').trim().length === 0) continue;
    const lineHeight = node.parentElement ? parseFloat(getComputedStyle(node.parentElement).lineHeight) : Number.NaN;
    range.selectNodeContents(node);
    for (const rect of Array.from(range.getClientRects())) {
      if (rect.height <= 0) continue;
      // The bottom of the LINE BOX, not of the glyph box inside it: the true edge between two lines.
      const half = Number.isFinite(lineHeight) ? Math.max(lineHeight, rect.height) / 2 : rect.height * 0.6;
      lineBottoms.push((rect.top + rect.bottom) / 2 + half - origin);
    }
  }
  const extent = (el: Element, keepWithNext = 0): [number, number] => {
    const rect = el.getBoundingClientRect();
    return [rect.top - origin, rect.bottom - origin + keepWithNext];
  };
  const atomic = [...Array.from(content.querySelectorAll(UNBREAKABLE)).map((el) => extent(el)), ...Array.from(content.querySelectorAll(HEADINGS)).map((el) => extent(el, KEEP_WITH_NEXT_PX))];
  return { lineBottoms, atomic };
}

export const jsPdfRenderer: PdfRenderer = {
  async render(html: string): Promise<PdfResult> {
    bindOptionalLibraries();
    const probeFix = document.createElement('style');
    probeFix.textContent = FONT_PROBE_FIX;
    document.head.appendChild(probeFix);
    const host = document.createElement('div');
    host.setAttribute('data-report-render', 'true');
    host.style.position = 'fixed';
    host.style.left = '0';
    host.style.top = '0';
    host.style.width = `${LAYOUT_WIDTH_PX}px`;
    host.style.background = '#ffffff';
    host.style.zIndex = '-1';
    host.style.opacity = '0';
    host.style.pointerEvents = 'none';
    // iPhone check of the fix above (2026-10-26): iOS text autosizing enlarged the paragraphs of this 720px
    // document on a 430px screen — but not its table cells — so the PDF came out with mixed type sizes and
    // 27 pages where Chromium draws 19. The document is laid out at its own width, never inflated.
    host.style.setProperty('-webkit-text-size-adjust', '100%');
    host.style.setProperty('text-size-adjust', '100%');
    const content = document.createElement('div');
    // Parsed inert first (a <template> loads nothing), so the pictures load from their blob: URLs only.
    const parsed = document.createElement('template');
    parsed.innerHTML = html;
    const blobUrls = pictureSourcesToBlobUrls(parsed.content);
    // 2026-10-28: an English document (its root asks for a text layer) is drawn as real text, photos and shapes
    // (./pdfVector.ts), laid out in the typeface the PDF draws; any other document stays one picture per page.
    // A few Chinese characters (a client's name) are drawn as small pictures; a document with much Chinese —
    // the bilingual agreement of an older copy — keeps its page pictures.
    const cjk = (parsed.content.textContent ?? '').match(/[㐀-鿿豈-﫿]/g)?.length ?? 0;
    const vector = parsed.content.querySelector<HTMLElement>('.rp, .ag')?.dataset.textLayer === 'true' && cjk <= VECTOR_MAX_CJK;
    const vectorFont = vector ? document.createElement('style') : null;
    if (vectorFont) {
      vectorFont.textContent = VECTOR_FONT_CSS;
      document.head.appendChild(vectorFont);
    }
    content.appendChild(parsed.content);
    host.appendChild(content);
    document.body.appendChild(host);
    try {
      await rasterizeSvgBackgrounds(content);
      await imagesDecoded(content);
      if (document.fonts?.ready) await document.fonts.ready;
      const total = Math.ceil(content.getBoundingClientRect().height);
      const pages = paginate({ total, pageHeight: PDF_PAGE_HEIGHT_PX, ...measureBreaks(content) });
      const pictures = freezePictures(content);
      const chunks = chunkPages(pages, Math.floor(PDF_MAX_CANVAS_PIXELS / (LAYOUT_WIDTH_PX * SCALE * SCALE)));
      const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait', compress: true });
      // 2026-10-19 (Owner item 20): what the report root asks for — the running header / footer, and the
      // text layer (English documents only; Android acceptance 2026-10-21 issue 10: the agreement too). Since
      // 2026-10-28 that text is the real, visible text of the page (./pdfVector.ts).
      const root = content.querySelector<HTMLElement>('.rp, .ag');
      const runningHeader = root?.dataset.runningHeader ?? '';
      const runningFooter = root?.dataset.runningFooter ?? '';
      const pageLabel = root?.dataset.pageLabel ?? '';
      // The running header / footer of page n: real text where Helvetica can say it, else a picture strip.
      const drawMargins = (pageNumber: number) => {
        if (!runningHeader && !runningFooter) return;
        const stripHeightPt = STRIP_HEIGHT_PX * PT_PER_PX;
        const pageText = pageLabel ? `${pageLabel} ${pageNumber} / ${pages.length}` : `${pageNumber} / ${pages.length}`;
        const headerArea = { x: MARGIN_PT, y: MARGIN_PT - stripHeightPt - 6, w: CONTENT_WIDTH_PT, h: stripHeightPt };
        const footerArea = { x: MARGIN_PT, y: PAGE_HEIGHT_PT - MARGIN_BOTTOM_PT + 10, w: CONTENT_WIDTH_PT, h: stripHeightPt };
        const marginPt = 10 * PT_PER_PX;
        if (!(vector && drawMarginText(doc, runningHeader, pageText, headerArea, marginPt))) {
          const header = marginStrip(runningHeader, pageText);
          if (header) doc.addImage(header, 'PNG', headerArea.x, headerArea.y, headerArea.w, headerArea.h);
        }
        if (runningFooter && !(vector && drawMarginText(doc, runningFooter, '', footerArea, marginPt))) {
          const footer = marginStrip(runningFooter, '');
          if (footer) doc.addImage(footer, 'PNG', footerArea.x, footerArea.y, footerArea.w, footerArea.h);
        }
      };

      if (vector) {
        // Measured while the document is laid out as printed: words, boxes, photos, inline SVG, the watermark.
        const origin = { x: MARGIN_PT, y: MARGIN_PT };
        const runs = collectTextRuns(content);
        const boxes = collectBoxes(content);
        const photos = measurePhotos(pictures, content);
        const svgs = await rasterizeSvgElements(content, SCALE * 1.5);
        const watermark = await watermarkOf(root ?? null, content);
        const encodedPhotos = new Map<string, EncodedPhoto | null>();
        // The decoded pictures in the laid-out copy are no longer needed (each photo is read once, when drawn).
        showPicturesOf(pictures, -1, -1);
        for (let index = 0; index < pages.length; index += 1) {
          const page = pages[index];
          if (index > 0) doc.addPage();
          if (watermark) drawWatermark(doc, watermark, page, origin, PT_PER_PX);
          drawBoxes(doc, boxes, page, origin, PT_PER_PX);
          drawSvgPictures(doc, svgs, page, origin, PT_PER_PX);
          await drawPhotos(doc, photos, page, origin, PT_PER_PX, SCALE, encodedPhotos);
          drawTextRuns(doc, runs, page, origin, PT_PER_PX, SCALE);
          drawMargins(index + 1);
        }
        return { bytes: doc.output('arraybuffer'), pageCount: doc.getNumberOfPages() };
      }

      let pageNumber = 0;
      for (const chunk of chunks) {
        const height = chunk.end - chunk.start;
        showPicturesOf(pictures, chunk.start, chunk.end);
        await imagesDecoded(content);
        const canvas = await html2canvas(host, {
          scale: SCALE,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
          width: LAYOUT_WIDTH_PX,
          height,
          windowWidth: LAYOUT_WIDTH_PX,
          windowHeight: height,
          scrollX: 0,
          scrollY: 0,
          // Only the copy is shown — clipped to this chunk and shifted up to it.
          onclone: (_clonedDocument, clone) => {
            clone.style.opacity = '1';
            clone.style.height = `${height}px`;
            clone.style.overflow = 'hidden';
            const shifted = clone.firstElementChild;
            if (shifted instanceof HTMLElement) shifted.style.marginTop = `${-chunk.start}px`;
          }
        });
        for (const page of chunk.pages) {
          const slice = document.createElement('canvas');
          slice.width = canvas.width;
          slice.height = Math.round((page.end - page.start) * SCALE);
          slice.getContext('2d')?.drawImage(canvas, 0, Math.round((page.start - chunk.start) * SCALE), canvas.width, slice.height, 0, 0, slice.width, slice.height);
          if (pageNumber > 0) doc.addPage();
          pageNumber += 1;
          // JPEG 0.8: no visible loss on text at this resolution, about 30% smaller than 0.92 (measured
          // 2026-09-12: an agreement page 631 KB → 443 KB) — a long report still fits an e-mail.
          doc.addImage(slice.toDataURL('image/jpeg', 0.8), 'JPEG', MARGIN_PT, MARGIN_PT, CONTENT_WIDTH_PT, (page.end - page.start) * PT_PER_PX, `page-${pageNumber}`);
          drawMargins(pageNumber);
          // release the pixels now, not whenever the collector gets to them (iOS counts canvas memory)
          slice.width = 0;
          slice.height = 0;
        }
        canvas.width = 0;
        canvas.height = 0;
      }
      return { bytes: doc.output('arraybuffer'), pageCount: doc.getNumberOfPages() };
    } finally {
      host.remove();
      probeFix.remove();
      vectorFont?.remove();
      resetWordPictures();
      for (const url of blobUrls) URL.revokeObjectURL(url);
    }
  }
};
